import {
  PERMANENT_DNI_EXPIRATION_DAY,
  PERMANENT_DNI_EXPIRATION_MONTH,
  PERMANENT_DNI_EXPIRATION_YEAR,
} from './constants.mjs';

/**
 * Sets a cookie or falls back to sessionStorage if cookies are not supported.
 *
 * @param {string} name - The name of the cookie or sessionStorage item.
 * @param {string} value - The value to be stored.
 * @param {number} [days] - The number of days until the cookie expires. If not provided, the cookie will be a session cookie.
 * @throws {Error} If cookies are not supported.
 */
export function setCookieOrSessionStorage(name, value, days, session=false) {
  if (session) {
    sessionStorage.setItem(name, value);
    return;
  }
  try {
    // Try to set the cookie
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";

    // Check if the cookie was set successfully
    if (document.cookie.indexOf(name + "=") === -1) {
      throw new Error("Cookies are not supported");
    }
  } catch (e) {
    // Fallback to sessionStorage
    sessionStorage.setItem(name, value);
  }
}

/**
 * Gets a cookie or falls back to sessionStorage if cookies are not supported.
 *
 * @param {string} name - The name of the cookie or sessionStorage item.
 * @returns {string|null} The value of the cookie or sessionStorage item, or null if not found.
 */
export function getCookieOrSessionStorage(name) {
  // Try to get the cookie
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }

  // Fallback to sessionStorage
  return sessionStorage.getItem(name);
}

/**
 * Validates if the given email address is in a proper format.
 *
 * @param {string} emailAddress - The email address to validate.
 * @returns {boolean} - Returns true if the email address is valid, otherwise false.
 */
export function isValidEmailAddress(emailAddress) {
  var pattern = new RegExp(/^(("[\w-+\s]+")|([\w-+]+(?:\.[\w-+]+)*)|("[\w-+\s]+")([\w-+]+(?:\.[\w-+]+)*))(@((?:[\w-+]+\.)*\w[\w-+]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][\d]\.|1[\d]{2}\.|[\d]{1,2}\.))((25[0-5]|2[0-4][\d]|1[\d]{2}|[\d]{1,2})\.){2}(25[0-5]|2[0-4][\d]|1[\d]{2}|[\d]{1,2})\]?$)/i);
  return pattern.test(emailAddress);
};

/**
 * Retrieves and parses JSON data from a script element with the specified ID.
 * This is a complementary function to the json_script Django template tag.
 *
 * @param {string} key - The ID of the script element containing JSON data.
 * @returns {Object} The parsed JSON data from the script element.
 * @throws {Error} If the element with the specified ID is not found or if the content is not valid JSON.
 */
export function getJsonScriptData(key) {
  const element = document.getElementById(key);
  if (!element) {
    throw new Error(`Element with id ${key} not found`);
  }
  try {
    return JSON.parse(element.textContent);
  } catch (e) {
    throw new Error(`Invalid JSON content in element with id ${key}`);
  }
}

/**
 * Parses date string from DD/MM/YYYY, DD-MM-YYYY (calendar input format) or YYYY-MM-DD (API format) to Date object
 *
 * @params {string} Date string from the date input field.
 * @returns {Date} Date object of the date string.
 */
export function parseDate(dateString) {
  if (!dateString || !dateString.trim()) return null;
  if (dateString.includes('-')) {
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    if (parts[0].length === 4) {
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }
  const parts = dateString.split('/');
  if (parts.length !== 3) return null;
  if (parts[0].length === 4) {
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }
  return new Date(parts[2], parts[1] - 1, parts[0]);
}

/**
 * Caducidad del DNI permanente de mayores: 01-01-9999 (también 01/01/9999 o 9999-01-01).
 * @param {string} dateString
 * @returns {boolean}
 */
export function isPermanentDniExpirationDate(dateString) {
  const parsedDate = parseDate(dateString);
  if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
    return false;
  }

  return (
    parsedDate.getFullYear() === PERMANENT_DNI_EXPIRATION_YEAR
    && parsedDate.getMonth() === PERMANENT_DNI_EXPIRATION_MONTH - 1
    && parsedDate.getDate() === PERMANENT_DNI_EXPIRATION_DAY
  );
}


/**
 * Capitalizes the first letter of a string.
 *
 * @param {string} string - The string to capitalize.
 * @returns {string} The capitalized string.
 */
export function capitalizeFirstLetter(string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}